
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowRight, Trash2, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Itinerary } from '@/app/lib/types';
import { useUser, useFirestore } from '@/firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';

export function ItineraryListItem({ itinerary }: { itinerary: Itinerary }) {
  const { user } = useUser();
  const firestore = useFirestore();
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!user || !firestore) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'You must be logged in to delete an itinerary.',
      });
      return;
    }
    setIsDeleting(true);
    try {
      const docRef = doc(firestore, 'users', user.uid, 'itineraries', itinerary.id);
      await deleteDoc(docRef);
      toast({
        title: 'Itinerary Deleted',
        description: `"${itinerary.name}" has been removed.`,
      });
    } catch (error) {
      console.error("Error deleting itinerary: ", error);
      toast({
        variant: 'destructive',
        title: 'Deletion Failed',
        description: 'There was a problem deleting your itinerary. Please try again.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Calculate duration in days
  const startDate = new Date(itinerary.startDate);
  const endDate = new Date(itinerary.endDate);
  const durationDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  return (
    <Card className="group flex flex-col overflow-hidden transition-smooth hover:shadow-loch-lg animate-fade-in">
      {/* Hero Image Section with Gradient Overlay */}
      <div className="relative h-40 sm:h-48 bg-gradient-to-br from-primary/20 via-accent/10 to-secondary/20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=800&h=400&fit=crop')] bg-cover bg-center opacity-40 group-hover:scale-110 transition-smooth" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Floating Badge */}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-primary shadow-loch">
          {durationDays} {durationDays === 1 ? 'Day' : 'Days'}
        </div>

        {/* Title Overlay on Image */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
          <h3 className="text-xl sm:text-2xl font-bold text-white drop-shadow-lg line-clamp-2">
            {itinerary.name}
          </h3>
        </div>
      </div>

      {/* Content Section */}
      <CardContent className="flex-grow p-4 sm:p-5 space-y-3">
        {/* Date Range */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="flex-1">
            <p className="font-medium text-foreground">{format(startDate, 'MMM d, yyyy')}</p>
            <p className="text-xs">Start Date</p>
          </div>
          <ArrowRight className="h-4 w-4 flex-shrink-0" />
          <div className="flex-1 text-right">
            <p className="font-medium text-foreground">{format(endDate, 'MMM d, yyyy')}</p>
            <p className="text-xs">End Date</p>
          </div>
        </div>

        {/* Trip Details */}
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
            {itinerary.skillLevel || 'Intermediate'}
          </span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20">
            {itinerary.preferences || 'Adventure'}
          </span>
        </div>
      </CardContent>

      {/* Action Footer */}
      <div className="border-t bg-muted/30 p-3 sm:p-4 flex justify-between items-center gap-3">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="touch-target text-destructive hover:text-destructive hover:bg-destructive/10 transition-smooth-fast"
            >
              <Trash2 className="h-5 w-5" />
              <span className="sr-only">Delete itinerary</span>
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this expedition?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete
                <span className="font-semibold text-foreground"> "{itinerary.name}"</span>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isDeleting}
                className="bg-destructive hover:bg-destructive/90"
              >
                {isDeleting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting...</>
                ) : (
                  'Delete Expedition'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button
          asChild
          className="flex-1 touch-target font-semibold group/btn"
        >
          <Link to={`/itinerary/${itinerary.id}`}>
            <span>View Expedition</span>
            <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-smooth-fast" />
          </Link>
        </Button>
      </div>
    </Card>
  );
}
